import { Button, AlertColor, Typography } from '@mui/material';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import parser from 'html-react-parser';

// Internal imports
import { FullPolicy, PolicyStatus } from '@acorn-portal/shared/src/services/api';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import PolicyAlert from '@acorn-portal/shared/src/components/alerts/Alert';
import { DISPLAY_DATE, DISPLAY_DATE_TIME } from '@acorn-portal/shared/src/utils/constants';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import LoadingModal from '../modal/LoadingModal';
import useGetRenewalDetails from '../../hooks/useGetRenewalDetails';

interface IRenewalAlerts {
	policy: FullPolicy;
}

const RenewalAlerts: FC<IRenewalAlerts> = ({ policy }) => {
	const navigate = useNavigate();
	const [loadingRenewal, setLoadingRenewal] = useState(false);
	const { getRenewalRating } = useGetRenewalDetails();

	const {
		policyStatus,
		renewalDetails,
		endDate: policyEndDate,
		autoRenewal,
		renewalDeclined,
		policyNumber,
		policyToken,
		product,
	} = policy;

	const isDueToRenew = policyStatus === PolicyStatus.DueForRenewal && renewalDetails?.renewalCoverDetails.startDate;
	const isDueToExpire = policyStatus === PolicyStatus.DueToExpire;
	const isEndOfProduct = policyStatus === PolicyStatus.EndOfProduct;
	const isRenewalDeclined = (renewalDeclined && autoRenewal) || isEndOfProduct;

	const endDateValue = renewalDetails?.renewalCoverDetails.startDate ?? policyEndDate;
	const formattedEndDate = format(new Date(endDateValue), DISPLAY_DATE);
	const formattedEndDateTime = format(new Date(endDateValue), DISPLAY_DATE_TIME);

	const getAlertType = (): AlertColor => {
		if ((!autoRenewal || renewalDeclined) && !isEndOfProduct) {
			return 'warning';
		}
		return 'info';
	};

	const getRenewalMessage = (): string => {
		if (isEndOfProduct) {
			return product === Product.CARROT
				? `We are unable to offer you a renewal quotation as all of the drivers on the policy are aged 25 and over. Your policy will therefore expire ${formattedEndDateTime}.`
				: `We are unable to offer you a renewal quotation as all of the drivers on the policy are aged 25 and over. However, we offer Private Car insurance with no telematics device, to get a
           quote, please click <a href="https://pc-quote.acorninsure.co.uk/quote/vehicle-details"
            target="_blank"
            rel="noreferrer">here</a>.`;
		}

		if (!autoRenewal || renewalDeclined) {
			return `Please be aware that your policy is set to expire on ${formattedEndDate}.`;
		}

		return `Please be aware that your policy is set to automatically renew on ${formattedEndDate}. No further action is needed at this stage. However you can renew your policy early.`;
	};

	const handleRenewNow = async () => {
		setLoadingRenewal(true);
		await getRenewalRating(
			policyToken,
			policyNumber,
			renewalDetails?.renewalCoverDetails.startDate || '',
			renewalDetails?.transactionId || '',
		);
		navigate(ROUTE_PATHS.renewalMakeAChangeSummary);
	};

	if (!(isDueToRenew || isDueToExpire || isRenewalDeclined || isEndOfProduct)) {
		return null;
	}

	const alertType = getAlertType();
	const alertTitle =
		isRenewalDeclined || isDueToExpire ? 'Your Policy Is Due To Expire' : 'Your Policy Is Due For Renewal';

	return (
		<>
			<LoadingModal open={loadingRenewal} message='Loading renewal details...' />

			<PolicyAlert type={alertType} title={alertTitle}>
				<Typography>{parser(getRenewalMessage())}</Typography>
				{isDueToRenew && (
					<Button
						onClick={handleRenewNow}
						sx={{ mt: 2, width: { xs: '100%', md: 'auto' }, right: 0 }}
						color={alertType}
						variant='outlined'
					>
						Renew Now
					</Button>
				)}
			</PolicyAlert>
		</>
	);
};

export default RenewalAlerts;
